import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {Inject} from 'inversify-props';

import {UpgradeStatus} from '@/model/system/update/UpgradeStatus';
import {UpgradeState} from '@/model/system/update/UpgradeState';

import {ISystemUpgradeService} from '@/services/system/IUpgrade.service';

@Module({name: 'SystemUpgradeStore', namespaced: true})
export default class SystemUpgradeStore extends VuexModule {

  private channel: string | null = null;

  private repository: string | null = null;

  private upgradeState: UpgradeState | null = UpgradeState.Idle;

  @Inject(Symbol.for('ISystemUpgradeService'))
  private systemUpgradeService!: ISystemUpgradeService;

  /* CHANNEL */
  @Action({commit: 'setChannel', rawError: true})
  public async fetchChannel() {
    return await this.systemUpgradeService.getChannel();
  }

  @Mutation
  public setChannel(channel: string) {
    this.channel = channel;
  }

  get Channel(): string | null {
    return this.channel;
  }

  /* REPOSITORY */
  @Action({commit: 'setRepository', rawError: true})
  public async fetchRepository() {
    return await this.systemUpgradeService.getRepository();
  }

  @Mutation
  public setRepository(repository: string) {
    this.repository = repository;
  }

  get Repository(): string | null {
    return this.repository;
  }

  /* STATE */
  @Action({commit: 'setState', rawError: true})
  public async fetchState() {

    const status = await this.systemUpgradeService.status();

    if (status === UpgradeStatus.Ephemeral) {
      return UpgradeState.Checking;
    }

    if (status === UpgradeStatus.Pending) {
      return UpgradeState.Pending;
    }

    if (status === UpgradeStatus.Downloaded) {
      return UpgradeState.Downloaded;
    }

    return this.upgradeState;
  }

  @Action({rawError: true})
  public async checkDownloaded() {
    if (await this.systemUpgradeService.status() === UpgradeStatus.Downloaded) {
      this.context.commit('setState', UpgradeState.Downloaded);
    }
  }

  @Action({rawError: true})
  public async checkPending() {
    if (await this.systemUpgradeService.status() === UpgradeStatus.Pending) {
      this.context.commit('setState', UpgradeState.Pending);
    }
  }

  @Action({rawError: true})
  public async checkEphemeral() {
    if (await this.systemUpgradeService.status() === UpgradeStatus.Ephemeral) {
      this.context.commit('setState', UpgradeState.Checking);
    }
  }

  @Mutation
  public setState(state: UpgradeState) {
    if (state === this.upgradeState) {
      return;
    }

    this.upgradeState = state;
  }

  get UpgradeState(): UpgradeState | null {
    return this.upgradeState;
  }
}
